type CaptureFormat = 'webp' | 'avif' | 'jpeg';
type ReferencePackAssetUploadPhase = 'break' | 'session_end' | 'reference_pack';

export interface QueueReferencePackAssetInput {
  roomCode: string;
  modelParticipantId: string;
  modelProfileId: string | null;
  blob: Blob;
  capturedAt: number;
  width: number;
  height: number;
  format: CaptureFormat;
  poseName: string | null;
  poseIndex: number | null;
  cameraId: string | null;
  cameraLabel: string | null;
}

interface QueuedReferencePackAsset extends QueueReferencePackAssetInput {
  id: string;
}

export interface ReferencePackAssetUploadProgress {
  hasOutstandingWork: boolean;
  totalCaptures: number;
  uploadedCaptures: number;
  queuedBytes: number;
  uploadedBytes: number;
  activeUploadBytes: number;
  activeUploadTotalBytes: number;
}

export const EMPTY_REFERENCE_PACK_ASSET_UPLOAD_PROGRESS: ReferencePackAssetUploadProgress = {
  hasOutstandingWork: false,
  totalCaptures: 0,
  uploadedCaptures: 0,
  queuedBytes: 0,
  uploadedBytes: 0,
  activeUploadBytes: 0,
  activeUploadTotalBytes: 0,
};

interface UploadQueuedReferencePackAssetsOptions {
  roomCode: string;
  uploadPhase: ReferencePackAssetUploadPhase;
  limit?: number;
  signal?: AbortSignal;
  onProgress?: (progress: ReferencePackAssetUploadProgress) => void;
}

interface UploadResponse {
  ok: boolean;
  status: number;
  text: string;
}

const DB_NAME = 'modelcast-reference-packs';
const STORE_NAME = 'captures';
const DB_VERSION = 1;

function referencePackAssetStorageAvailable() {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

async function requestPersistentReferencePackAssetStorage(): Promise<void> {
  if (typeof navigator === 'undefined' || !('storage' in navigator) || !navigator.storage.persist) return;

  try {
    const alreadyPersisted = await navigator.storage.persisted?.();
    if (!alreadyPersisted) {
      await navigator.storage.persist();
    }
  } catch (error) {
    console.warn('[ReferencePackAssetUploadQueue] Persistent storage request failed', { error });
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('roomCode', 'roomCode', { unique: false });
        store.createIndex('capturedAt', 'capturedAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function getAllCaptures(): Promise<QueuedReferencePackAsset[]> {
  const db = await openDb();

  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const captures = await new Promise<QueuedReferencePackAsset[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result as QueuedReferencePackAsset[]);
      request.onerror = () => reject(request.error);
    });

    await transactionDone(tx);
    return captures;
  } finally {
    db.close();
  }
}

async function deleteCapture(id: string): Promise<void> {
  const db = await openDb();

  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function enqueueReferencePackAsset(input: QueueReferencePackAssetInput): Promise<void> {
  if (!referencePackAssetStorageAvailable()) {
    console.warn('[ReferencePackAssetUploadQueue] IndexedDB is unavailable; capture cannot be queued', {
      roomCode: input.roomCode,
      modelParticipantId: input.modelParticipantId,
      capturedAt: input.capturedAt,
    });
    return;
  }

  await requestPersistentReferencePackAssetStorage();

  const db = await openDb();

  try {
    const capture: QueuedReferencePackAsset = {
      ...input,
      id: `${input.roomCode}-${input.capturedAt}-${crypto.randomUUID()}`,
    };

    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(capture);
    await transactionDone(tx);
    console.log('[ReferencePackAssetUploadQueue] Capture queued', {
      captureId: capture.id,
      roomCode: capture.roomCode,
      modelParticipantId: capture.modelParticipantId,
      modelProfileId: capture.modelProfileId,
      width: capture.width,
      height: capture.height,
      format: capture.format,
      sizeBytes: capture.blob.size,
      poseName: capture.poseName,
      poseIndex: capture.poseIndex,
      cameraId: capture.cameraId,
      cameraLabel: capture.cameraLabel,
    });
  } finally {
    db.close();
  }
}

export async function getQueuedReferencePackAssetProgress(roomCode: string): Promise<ReferencePackAssetUploadProgress> {
  if (!referencePackAssetStorageAvailable()) {
    return EMPTY_REFERENCE_PACK_ASSET_UPLOAD_PROGRESS;
  }

  const captures = await getAllCaptures();
  const roomCaptures = captures.filter((capture) => capture.roomCode === roomCode);
  const queuedBytes = roomCaptures.reduce((total, capture) => total + capture.blob.size, 0);

  return {
    hasOutstandingWork: roomCaptures.length > 0,
    totalCaptures: roomCaptures.length,
    uploadedCaptures: 0,
    queuedBytes,
    uploadedBytes: 0,
    activeUploadBytes: 0,
    activeUploadTotalBytes: 0,
  };
}

function uploadFormDataWithProgress(params: {
  url: string;
  formData: FormData;
  signal?: AbortSignal;
  onUploadProgress?: (loadedBytes: number) => void;
}): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const handleAbort = () => {
      xhr.abort();
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    if (params.signal?.aborted) {
      handleAbort();
      return;
    }

    xhr.open('POST', params.url);

    xhr.upload.onprogress = (event) => {
      params.onUploadProgress?.(event.loaded);
    };

    xhr.onload = () => {
      params.signal?.removeEventListener('abort', handleAbort);
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        text: xhr.responseText ?? '',
      });
    };

    xhr.onerror = () => {
      params.signal?.removeEventListener('abort', handleAbort);
      reject(new Error('Network error while uploading model capture'));
    };

    xhr.onabort = () => {
      params.signal?.removeEventListener('abort', handleAbort);
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    params.signal?.addEventListener('abort', handleAbort, { once: true });
    xhr.send(params.formData);
  });
}

export async function getQueuedReferencePackAssetCount(roomCode: string): Promise<number> {
  const progress = await getQueuedReferencePackAssetProgress(roomCode);
  return progress.totalCaptures;
}

export async function uploadQueuedReferencePackAssets({
  roomCode,
  uploadPhase,
  limit = 3,
  signal,
  onProgress,
}: UploadQueuedReferencePackAssetsOptions): Promise<number> {
  if (!referencePackAssetStorageAvailable()) {
    console.warn('[ReferencePackAssetUploadQueue] IndexedDB is unavailable; queued captures cannot upload', {
      roomCode,
      uploadPhase,
    });
    return 0;
  }

  onProgress?.(await getQueuedReferencePackAssetProgress(roomCode));

  const allCaptures = await getAllCaptures();
  const roomCaptures = allCaptures
    .filter((capture) => capture.roomCode === roomCode)
    .sort((a, b) => a.capturedAt - b.capturedAt);
  const captures = roomCaptures.slice(0, limit);

  console.log('[ReferencePackAssetUploadQueue] Upload pass starting', {
    roomCode,
    uploadPhase,
    limit,
    queuedForRoom: roomCaptures.length,
    selectedForUpload: captures.length,
  });

  let uploaded = 0;
  let uploadedBytes = 0;

  for (const capture of captures) {
    if (signal?.aborted) {
      console.log('[ReferencePackAssetUploadQueue] Upload pass aborted before capture upload', {
        roomCode,
        uploadPhase,
        uploaded,
      });
      break;
    }

    const extension = capture.format === 'jpeg' ? 'jpg' : capture.format;
    const formData = new FormData();
    formData.append('image', capture.blob, `${capture.id}.${extension}`);
    formData.append('participantId', capture.modelParticipantId);
    if (capture.modelProfileId) {
      formData.append('modelProfileId', capture.modelProfileId);
    }
    formData.append('capturedAt', new Date(capture.capturedAt).toISOString());
    formData.append('width', String(capture.width));
    formData.append('height', String(capture.height));
    formData.append('format', capture.format);
    formData.append('poseName', capture.poseName ?? '');
    formData.append('poseIndex', capture.poseIndex === null ? '' : String(capture.poseIndex));
    formData.append('cameraId', capture.cameraId ?? '');
    formData.append('cameraLabel', capture.cameraLabel ?? '');
    formData.append('uploadPhase', uploadPhase);

    console.log('[ReferencePackAssetUploadQueue] Uploading capture', {
      captureId: capture.id,
      roomCode,
      uploadPhase,
      width: capture.width,
      height: capture.height,
      format: capture.format,
      sizeBytes: capture.blob.size,
      modelParticipantId: capture.modelParticipantId,
      modelProfileId: capture.modelProfileId,
      capturedAt: new Date(capture.capturedAt).toISOString(),
      cameraId: capture.cameraId,
      cameraLabel: capture.cameraLabel,
    });

    const progressBeforeCapture = await getQueuedReferencePackAssetProgress(roomCode);
    const response = await uploadFormDataWithProgress({
      url: `/api/products/reference-packs/sessions/${roomCode}/assets`,
      formData,
      signal,
      onUploadProgress: (loadedBytes) => {
        onProgress?.({
          ...progressBeforeCapture,
          uploadedCaptures: uploaded,
          uploadedBytes,
          activeUploadBytes: Math.min(capture.blob.size, loadedBytes),
          activeUploadTotalBytes: capture.blob.size,
        });
      },
    });

    if (!response.ok) {
      console.error('[ReferencePackAssetUploadQueue] Capture upload failed', {
        captureId: capture.id,
        roomCode,
        uploadPhase,
        status: response.status,
        body: response.text,
      });
      throw new Error(`Model capture upload failed with status ${response.status}`);
    }

    await deleteCapture(capture.id);
    uploaded++;
    uploadedBytes += capture.blob.size;
    onProgress?.({
      ...(await getQueuedReferencePackAssetProgress(roomCode)),
      uploadedCaptures: uploaded,
      uploadedBytes,
    });
    console.log('[ReferencePackAssetUploadQueue] Capture uploaded and removed from queue', {
      captureId: capture.id,
      roomCode,
      uploadPhase,
      uploaded,
    });
  }

  console.log('[ReferencePackAssetUploadQueue] Upload pass complete', {
    roomCode,
    uploadPhase,
    uploaded,
    remainingSelected: captures.length - uploaded,
  });
  return uploaded;
}
